// The two behaviours on the mock request form: which step is showing, and
// which lane's fieldsets are showing. Replaces past_paper_request.js, which
// owned the second of those alone.
//
// NOT IN packs/, for the reason mock_composer.js records — every file under
// that directory becomes its own webpack entrypoint and emits a bundle no
// layout loads. Imported by packs/application.js, like course_finder.js and
// mock_composer.js before it.
//
// No framework. This app has neither Turbo nor Stimulus, deliberately, and
// setup() early-returns when its markup is absent so it costs nothing on the
// other ~200 pages the pack is emitted on.
//
// HIDES, NEVER DISABLES, and that is the whole contract. Every field on every
// step posts on every submission regardless of what is on screen, and
// MockRequest's own validations — which fire only `if: :past_paper?` or
// `if: :textbook?` — decide whether they were needed. With the pack absent,
// the listener broken, or JavaScript off, every panel is on the page and the
// form still works: the student sees all four steps and fills in what applies.
// The step buttons arrive `hidden` in the markup for the same reason — they
// do nothing without this file, so this file is what reveals them.
//
// `hidden` rather than style.display, so [hidden] does the work and nothing
// has to remember which display value to restore.
//
// Mocks::RequestWizard is the one declaration of the steps. Nothing here
// knows how many there are: the panels and the rail are read off the page,
// and `data-wizard-start` says which to open — the step a failed save wants
// the student back on, or 1.

function setup() {
  const form = document.querySelector("form.wizard[data-wizard-start]");
  if (form === null) return;

  const panels = Array.from(form.querySelectorAll(".wizard__panel"));
  const rail = Array.from(form.querySelectorAll(".wizard__step"));
  if (panels.length === 0) return;

  // --- the lane ------------------------------------------------------------

  const kinds = form.querySelectorAll(".js-request-kind");
  const lanes = form.querySelectorAll("[data-lane]");

  const syncLane = () => {
    const chosen = form.querySelector(".js-request-kind:checked");
    const kind = chosen === null ? "topic_led" : chosen.value;
    lanes.forEach((lane) => { lane.hidden = lane.dataset.lane !== kind; });
  };

  kinds.forEach((radio) => radio.addEventListener("change", syncLane));

  // --- the step ------------------------------------------------------------

  const stepOf = (panel) => Number(panel.dataset.step);
  const last = Math.max(...panels.map(stepOf));
  let current = Number(form.dataset.wizardStart) || 1;

  const show = (step, { scroll = true } = {}) => {
    current = Math.min(Math.max(step, 1), last);

    panels.forEach((panel) => { panel.hidden = stepOf(panel) !== current; });
    rail.forEach((item) => {
      const n = Number(item.dataset.step);
      item.classList.toggle("is-active", n === current);
      item.classList.toggle("is-done", n < current);
    });

    // Scrolling to the rail rather than the top of the page keeps the
    // heading in view on a phone, where the intro paragraph is a screenful.
    const railEl = form.querySelector(".wizard__rail");
    if (scroll && railEl) railEl.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  // A step may only be left forwards when what is VISIBLE on it is valid —
  // the browser's own `required` on the exam and subject boxes, and nothing
  // else, since the lane fields are the server's to judge. reportValidity()
  // shows the browser's message beside the box; no alert(), which is the one
  // thing about the quiz generator's wizard not worth copying.
  const visibleFieldsIn = (panel) =>
    Array.from(panel.querySelectorAll("input, select, textarea"))
      .filter((field) => field.closest("[hidden]") === null);

  const panelIsValid = (panel) =>
    visibleFieldsIn(panel).every((field) => field.reportValidity());

  const currentPanel = () => panels.find((panel) => stepOf(panel) === current);

  const next = () => {
    if (!panelIsValid(currentPanel())) return;
    show(current + 1);
  };

  form.querySelectorAll(".wizard__next").forEach((button) => {
    button.hidden = false;
    button.addEventListener("click", next);
  });

  // Enter in a text box is implicit submission, and the browser aims it at
  // the form's first submit button whether or not that button is on screen —
  // `hidden` is not `disabled`. With the Next buttons being type="button",
  // that made Enter in the subject box on step 2 file the whole request with
  // steps 3 and 4 never shown. On every step but the last, Enter is Next.
  // Only from the boxes that submit implicitly: a textarea keeps Enter (a
  // newline is what it means there), a button keeps its own click, and the
  // last step keeps the submit, which is the button Enter should reach.
  const submitsImplicitly = "input:not([type=checkbox]):not([type=radio]):not([type=file])";

  form.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" || current === last) return;
    if (!event.target.matches(submitsImplicitly)) return;
    event.preventDefault();
    next();
  });

  form.querySelectorAll(".wizard__prev").forEach((button) => {
    button.hidden = false;
    button.addEventListener("click", () => show(current - 1));
  });

  syncLane();
  show(current, { scroll: false });
}

// The same guard mock_composer.js and course_finder.js carry, kept here so all
// the modules in this pack start the same way. What it buys is independence
// from how the pack is loaded: if `application` is ever split and this module
// arrives in an async chunk, DOMContentLoaded is long gone and only the
// readyState branch still calls setup().
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setup);
} else {
  setup();
}
